import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, XCircle, DollarSign, Calendar, User, FileText, AlertCircle, Upload, Trash2 } from "lucide-react";
import { Button } from "./Button";
import { escrowAPI, EscrowStatus } from "../../services/escrowAPI";
import { useAppSelector, useAppDispatch } from "../../redux";
import { RoleEnum, MilestonePaymentStatusEnum } from "../../modules";
import { createMilestonePaymentLog, fetchMilestonePaymentLogsByMilestone, updateContract, updateMilestonePaymentLog, uploadContractFile } from "../../redux/Slices/contractSlice";
import { MilestonePaymentLogCreateDto, MilestonePaymentLog, ContractStatusEnum } from "../../modules/shared/model";
import { createChat, createConversation, fetchParticipantChats, updateChat } from "../../redux";
import { ContextEnum, ConversationTypeEnum } from "../../modules/shared/model";
import { useWebSocket } from "../../hooks/useWebSocket";
import { GlobalService } from "../../modules/shared/services/global.service";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface PaymentRequestModalProps {
    isOpen: boolean;
    onClose: (messageData?: any) => void;
    paymentRequest: any;
    loading?: boolean;
    milestoneId?: string; // Optional milestone ID for updating milestone status
}

const PaymentRequestModal: React.FC<PaymentRequestModalProps> = ({
    isOpen,
    onClose,
    paymentRequest,
    loading = false,
    milestoneId
}) => {
    const { t } = useTranslation();
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [reasonAttachments, setReasonAttachments] = useState<Array<{ id: string; name: string; path: string; file?: File }>>([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [milestonePaymentLogs, setMilestonePaymentLogs] = useState<MilestonePaymentLog[]>([]);
    const [latestPaymentLog, setLatestPaymentLog] = useState<MilestonePaymentLog | null>(null);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [isUpdatingEscrow, setIsUpdatingEscrow] = useState(false);
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [overallLoading, setOverallLoading] = useState(false);
    const { user } = useAppSelector((state: any) => state.auth);
    const { current: currentContract } = useAppSelector((s: any) => s.contracts);
    const dispatch = useAppDispatch();
    const { sendMessage: sendWebSocketMessage } = useWebSocket();
    const navigate = useNavigate();
    const location = useLocation();

  const getPaymentRequestStatusKey = (status?: string) => {
    const normalized = status?.toLowerCase();
    if (!normalized) {
      return "pending";
    }
    switch (normalized) {
      case "approved":
      case "rejected":
      case "pending":
        return normalized;
      default:
        return "pending";
    }
  };

  const getPaymentRequestStatusLabel = (status?: string) => {
    const key = getPaymentRequestStatusKey(status);
    return t(`paymentRequestModal.statuses.${key}`);
  };

  const getPaymentRequestStatusClasses = (status?: string) => {
    const key = getPaymentRequestStatusKey(status);
    switch (key) {
      case "approved":
        return "text-green-600";
      case "rejected":
        return "text-red-600";
      default:
        return "text-yellow-600";
    }
  };

  const getMilestoneStatusLabel = (statusId?: number | null) => {
    switch (Number(statusId)) {
      case MilestonePaymentStatusEnum.Released:
        return t("paymentRequestModal.milestoneStatuses.released");
      case MilestonePaymentStatusEnum.Rejected:
        return t("paymentRequestModal.milestoneStatuses.rejected");
      case MilestonePaymentStatusEnum.Requested:
        return t("paymentRequestModal.milestoneStatuses.requested");
      case MilestonePaymentStatusEnum.Funded:
        return t("paymentRequestModal.milestoneStatuses.funded");
      case MilestonePaymentStatusEnum.Pending:
        return t("paymentRequestModal.milestoneStatuses.pending");
      case MilestonePaymentStatusEnum.Accepted:
        return t("paymentRequestModal.milestoneStatuses.accepted");
      case MilestonePaymentStatusEnum.Completed:
        return t("paymentRequestModal.milestoneStatuses.completed");
      case MilestonePaymentStatusEnum.Archived:
        return t("paymentRequestModal.milestoneStatuses.archived");
      case MilestonePaymentStatusEnum.Suspended:
        return t("paymentRequestModal.milestoneStatuses.suspended");
      case MilestonePaymentStatusEnum.Deleted:
        return t("paymentRequestModal.milestoneStatuses.deleted");
      case MilestonePaymentStatusEnum.Paused:
        return t("paymentRequestModal.milestoneStatuses.paused");
      case MilestonePaymentStatusEnum.Expired:
        return t("paymentRequestModal.milestoneStatuses.expired");
      case MilestonePaymentStatusEnum.Failed:
        return t("paymentRequestModal.milestoneStatuses.failed");
      default:
        return t("paymentRequestModal.statusMessages.noStatus");
    }
  };

    // Reset all state to initial values
    const resetAllState = () => {
        setShowRejectForm(false);
        setRejectReason("");
        setIsSubmitting(false);
        setIsAccepting(false);
        setIsRejecting(false);
        setReasonAttachments([]);
        setUploadingFiles(false);
        setMilestonePaymentLogs([]);
        setLatestPaymentLog(null);
        setLoadingLogs(false);
        setIsUpdatingEscrow(false);
        setIsCreatingChat(false);
        setOverallLoading(false);
    };

    // Enhanced close handler that resets all state
    const handleClose = (messageData?: any) => {
        resetAllState();
        onClose(messageData);
    };

    // Reset state when modal is closed
    useEffect(() => {
        if (!isOpen) {
            // Reset state when modal is closed
            setTimeout(() => {
                resetAllState();
            }, 300); // Small delay to allow for smooth closing animation
        }
    }, [isOpen]);

    // Fetch milestone payment logs when modal opens
    useEffect(() => {
        const fetchPaymentLogs = async () => {
            if (milestoneId && isOpen) {
                setLoadingLogs(true);
                try {
                    const result = await dispatch(fetchMilestonePaymentLogsByMilestone(milestoneId)).unwrap();
                    const logs = result.data || [];
                    setMilestonePaymentLogs(logs);

                    // Get the latest payment log (most recent by createdAt)
                    if (logs.length > 0) {
                        // Create a copy before sorting to avoid mutating the original array
                        const logsCopy = [...logs];
                        const latest = logsCopy.sort((a, b) =>
                            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                        )[0];
                        setLatestPaymentLog(latest);
                        console.log("Latest payment log:", latest);
                    } else {
                        setLatestPaymentLog(null);
                    }
                } catch (error) {
                    console.error("Error fetching milestone payment logs:", error);
                    setLatestPaymentLog(null);
                } finally {
                    setLoadingLogs(false);
                }
            }
        };

        fetchPaymentLogs();
    }, [milestoneId, dispatch, isOpen]);

    if (!isOpen || !paymentRequest) return null;

    // Helper function to get current payment status
    const getCurrentPaymentStatus = () => {
        if (!latestPaymentLog) return null;
        return latestPaymentLog.paymentStatus?.id;
    };

    // Helper function to check if payment can be accepted
    const canAcceptPayment = () => {
        const status = getCurrentPaymentStatus();
        return !status || status === MilestonePaymentStatusEnum.Pending || status === MilestonePaymentStatusEnum.Rejected;
    };

    // Helper function to check if payment can be rejected
    const canRejectPayment = () => {
        const status = getCurrentPaymentStatus();
        return !status || status === MilestonePaymentStatusEnum.Pending || status === MilestonePaymentStatusEnum.Requested;
    };

    // File upload handler using uploadContractFile action
    const handleFileUpload = async (files: FileList) => {
        setUploadingFiles(true);
        try {
            const uploadPromises = Array.from(files).map(async (file) => {
                const { payload } = await dispatch(uploadContractFile(file) as any);
                const fileId = payload?.file?.id;
                const filePath = payload?.file?.path;

                if (fileId) {
                    return {
                        id: fileId,
                        name: file.name,
                        path: filePath || URL.createObjectURL(file),
                        file: file
                    };
                } else {
                    throw new Error(`Failed to upload ${file.name}`);
                }
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            setReasonAttachments(prev => [...prev, ...uploadedFiles]);
        } catch (error) {
            console.error("Error uploading files:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("toast.upload.fileUploadFailed"));
        } finally {
            setUploadingFiles(false);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileUpload(files);
        }
    };

    const removeAttachment = (index: number) => {
        setReasonAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Function to create chat and send payment request action message
    const createChatAndSendMessage = async (action: "accept" | "reject", reason?: string) => {
        setIsCreatingChat(true);
        try {
            // Get client and professional IDs from payment request
            const clientId = paymentRequest.escrow?.client?.id;
            const professionalId = paymentRequest.escrow?.professional?.id;
      const messageKey =
        action === "accept"
          ? "paymentRequestModal.notifications.accepted"
          : "paymentRequestModal.notifications.rejected";
      const message = t(messageKey);

            if (!clientId || !professionalId) {
                console.warn("Missing client or professional ID in payment request, skipping conversation message");
                return null;
            }

            // Check if chat already exists between client and professional
            let existingChat = null;
            try {
                existingChat = await dispatch(fetchParticipantChats({ currentUserId: clientId, page: 1, limit: 50 })).unwrap();
            } catch (error) {
                // No existing chat found, will create new one
            }

            let chatResponse;
            if (existingChat.chats && existingChat.chats.length > 0) {
                const filteredChats = existingChat.chats.filter((chat: any) => {
                    const participantIds = chat.chat.participants.map((p: any) => p.id);
                    return participantIds.includes(clientId) && participantIds.includes(professionalId);
                });
                const chat = filteredChats[0].chat;
                chatResponse = chat;

                // Update the chat with lastMessage and lastMessageAt
                if (chatResponse && chatResponse.id) {
                    const updatedChat = {
                        lastMessage: message,
                        lastMessageAt: new Date().toISOString(),
                        unreadCount: { [clientId]: user?.id === professionalId ? 1 : 0, [professionalId]: user?.id === clientId ? 1 : 0 },
                    };
                    await dispatch(updateChat({
                        chatId: chatResponse.id,
                        data: updatedChat
                    }));
                }
            } else {
                // Create new chat
                const chatPayload = {
                    unreadCount: { [clientId]: user?.id === professionalId ? 0 : 1, [professionalId]: user?.id === clientId ? 0 : 1 },
                    participants: [
                        { id: clientId },
                        { id: professionalId }
                    ],
                    contextId: paymentRequest.escrow?.contract?.id,
                    contextType: { id: ContextEnum.Message },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: message,
                };

                chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
            }

            // Send conversation message
            const conversationType = action === "accept" ? ConversationTypeEnum.Payment_Request_Accepted : ConversationTypeEnum.Payment_Request_Rejected;

            const conversationPayload = {
                isRead: false,
                message: message,
                sender: { id: user?.id },
                chatId: { id: chatResponse.id },
                type: { id: conversationType },
                contextId: { id: paymentRequest.escrow?.id },
                attachments: []
            };

            const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();

            // Send WebSocket message to notify other participants (non-blocking)
            const messageData = {
                type: "new_conversation",
                detail: {
                    conversationId: conversation.id,
                    conversation: conversation,
                    chatId: conversation.chatId.id,
                    message: conversationPayload.message,
                    senderId: conversation.sender.id,
                    attachments: conversationPayload.attachments,
                    type: conversationPayload.type.id,
                    referenceId: conversationPayload.contextId.id,
                    timestamp: new Date().toISOString()
                }
            };

            setTimeout(async () => {
                try {
                    if (sendWebSocketMessage) {
                        // Get participants from the chat response - handle both direct participants and nested structure
                        const participants = chatResponse.participants || chatResponse.chat?.participants || [];
                        const receivers = participants.map((p: any) => p.id); // No filter here

                        if (receivers.length > 0) {
                            await sendWebSocketMessage({
                                receivers,
                                sender: user?.id || "",
                                type: "new_conversation",
                                detail: messageData.detail,
                                conversationId: conversationPayload.chatId.id
                            });
                            console.log("📤 WebSocket message sent for payment request action");
                        }
                    }
                } catch (wsError) {
                    console.warn("⚠️ Failed to send WebSocket message for payment request action:", wsError);
                }
            }, 100);

            return messageData;
        } catch (error) {
            console.error("Error creating chat or sending message:", error);
            return null;
        } finally {
            setIsCreatingChat(false);
        }
    };

    const handleAccept = async () => {
        setIsAccepting(true);
        setOverallLoading(true);
        try {
            // Update or create milestone payment log for released status FIRST
            if (milestoneId) {
                if (latestPaymentLog) {
                    console.log("Updating milestone payment log:", {
                        milestoneId,
                        paymentLogId: latestPaymentLog.id,
                        currentStatus: latestPaymentLog.paymentStatus?.id,
                        newStatus: MilestonePaymentStatusEnum.Released
                    });

                    const updateData = {
                        paymentStatus: { id: MilestonePaymentStatusEnum.Released },
                      reason: t("paymentRequestModal.reasons.releasedByClient") as string
                    };

                    await dispatch(updateMilestonePaymentLog({
                        id: latestPaymentLog.id,
                        data: updateData
                    })).unwrap();
                    console.log("Milestone payment log updated for released status");
                } else {
                    console.log("No existing payment log found, creating new one for milestone:", milestoneId);
                    const paymentLogData: MilestonePaymentLogCreateDto = {
                        milestone: { id: milestoneId },
                        paymentStatus: { id: MilestonePaymentStatusEnum.Released },
                      reason: t("paymentRequestModal.reasons.releasedByClient") as string
                    };

                    await dispatch(createMilestonePaymentLog(paymentLogData)).unwrap();
                    console.log("Milestone payment log created for released status");
                }

                // Refresh payment logs to get updated data
                const result = await dispatch(fetchMilestonePaymentLogsByMilestone(milestoneId)).unwrap();
                const logs = result.data || [];
                setMilestonePaymentLogs(logs);

                if (logs.length > 0) {
                    // Create a copy before sorting to avoid mutating the original array
                    const logsCopy = [...logs];
                    const latest = logsCopy.sort((a, b) =>
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    )[0];
                    setLatestPaymentLog(latest);
                }
            }

            // Update escrow status to released (3) AFTER updating the log
            if (paymentRequest.escrow?.id) {
                setIsUpdatingEscrow(true);
                try {
                    await escrowAPI.updateEscrow(paymentRequest.escrow.id, { status: "released" });

                    // Check if contract should be closed
                    if (currentContract && currentContract.escrows.length > 0 && currentContract.escrows.length === currentContract.milestones.length) {
                        // Check if all other escrows (excluding the current one being released) are already released
                        const otherEscrows = currentContract.escrows.filter(escrow => escrow.id !== paymentRequest.escrow.id);
                        console.log("Other escrows:", otherEscrows);
                        let allOtherEscrowsReleased = false;
                        if (otherEscrows.length > 0) {
                            allOtherEscrowsReleased = otherEscrows.every(escrow =>
                                Number(escrow.status?.id) === EscrowStatus.RELEASED
                            );
                        }

                        if (allOtherEscrowsReleased || otherEscrows.length === 0) {
                            console.log("All other escrows are released, closing contract");
                            await dispatch(updateContract({
                                id: currentContract.id,
                                data: {
                                    status: { id: ContractStatusEnum.Closed }
                                }
                            }));
                            
                            // Handle contract closure navigation and tab selection
                            const currentPath = location.pathname;
                            
                            // Set localStorage to activate closed tab
                            localStorage.setItem("myOrdersSelectedTab", "closed");
                            
                            if (currentPath !== "/client/myorders") {
                                // Close all dialogs and redirect to myorders page
                                handleClose();
                                navigate("/client/myorders");
                            } else {
                                console.log("Already on myorders page, closing all dialogs and activating closed tab");
                                
                                // Already on myorders page - close all dialogs and activate closed tab
                                // Trigger a custom event to notify the parent to switch to closed tab
                                window.dispatchEvent(new CustomEvent("switchToClosedTab"));
                                
                                // Close all dialogs
                                handleClose();
                            }
                        } else {
                            console.log("Not all other escrows are released yet, contract remains open");
                        }
                    }
                    console.log("Escrow status updated to released (3) successfully");
                } catch (escrowError) {
                    console.error("Error updating escrow status:", escrowError);
                    // Don't throw here as milestone payment log was already updated
                    // Just show a warning that escrow status update failed
                    GlobalService.showToast("warning", t("toast.titles.warning"), t("toast.payments.requestLogWarning"));
                } finally {
                    setIsUpdatingEscrow(false);
                }
            } else {
                console.warn("No escrow ID found in payment request, skipping escrow status update");
            }

            // TODO: Implement API call to accept payment request
            console.log("Accepting payment request:", paymentRequest.id);
            // Example API call:
            // await dispatch(acceptPaymentRequest(paymentRequest.id));

            // Send WebSocket message and get message data
            const messageData = await createChatAndSendMessage("accept");

            // Show success message
            GlobalService.showToast("success", t("toast.titles.success"), t("toast.payments.requestAcceptedAndUpdated"));

            // Close modal immediately with message data
            handleClose(messageData);

        } catch (error) {
            console.error("Error accepting payment request:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("toast.payments.requestAcceptFailed"));
        } finally {
            setIsAccepting(false);
            setOverallLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            GlobalService.showToast("warning", t("toast.titles.warning"), t("toast.payments.provideRejectionReason"));
            return;
        }

        setIsRejecting(true);
        setOverallLoading(true);
        try {
            // Create milestone payment log for rejected status with reason and attachments
            if (milestoneId) {
                // Check if current status is already rejected - if so, create new record instead of updating
                const currentStatus = getCurrentPaymentStatus();
                const isAlreadyRejected = currentStatus === MilestonePaymentStatusEnum.Rejected;

                if (isAlreadyRejected) {
                    console.log("Current status is already rejected, creating new milestone payment log for rejection:", {
                        milestoneId,
                        currentStatus: currentStatus,
                        newStatus: MilestonePaymentStatusEnum.Rejected
                    });
                } else if (latestPaymentLog) {
                    console.log("Updating milestone payment log for rejection:", {
                        milestoneId,
                        paymentLogId: latestPaymentLog.id,
                        currentStatus: latestPaymentLog.paymentStatus?.id,
                        newStatus: MilestonePaymentStatusEnum.Rejected
                    });
                } else {
                    console.log("No existing payment log found, creating new one for rejection milestone:", milestoneId);
                }

                const paymentLogData: MilestonePaymentLogCreateDto = {
                    milestone: { id: milestoneId },
                    paymentStatus: { id: MilestonePaymentStatusEnum.Rejected },
                    reason: rejectReason,
                    reasonAttachments: reasonAttachments.map(attachment => ({ id: attachment.id }))
                };

                await dispatch(createMilestonePaymentLog(paymentLogData)).unwrap();
                console.log("Milestone payment log created for rejected status with reason and attachments");

                // Refresh payment logs to get updated data
                const result = await dispatch(fetchMilestonePaymentLogsByMilestone(milestoneId)).unwrap();
                const logs = result.data || [];
                setMilestonePaymentLogs(logs);

                if (logs.length > 0) {
                    // Create a copy before sorting to avoid mutating the original array
                    const logsCopy = [...logs];
                    const latest = logsCopy.sort((a, b) =>
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    )[0];
                    setLatestPaymentLog(latest);
                }
            }

            // TODO: Implement API call to reject payment request
            console.log("Rejecting payment request:", paymentRequest.id, "Reason:", rejectReason);
            // Example API call:
            // await dispatch(rejectPaymentRequest({ id: paymentRequest.id, reason: rejectReason }));

            // Send WebSocket message and get message data
            const messageData = await createChatAndSendMessage("reject", rejectReason);

            // Show success message
            GlobalService.showToast("success", t("toast.titles.success"), t("toast.payments.requestRejected"));

            // Close modal with message data
            handleClose(messageData);
        } catch (error) {
            console.error("Error rejecting payment request:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("toast.payments.requestRejectFailed"));
        } finally {
            setIsRejecting(false);
            setOverallLoading(false);
        }
    };

    const handleCancelReject = () => {
        setShowRejectForm(false);
        setRejectReason("");
        setReasonAttachments([]);
        setIsRejecting(false);
    };

    const formatCurrency = (amount: number, currency: string = "USD") => {
        // Map currency names to ISO 4217 codes
        const currencyMap: { [key: string]: string } = {
            "USD": "USD",
            "US Dollar": "USD",
            "EUR": "EUR",
            "Euro": "EUR",
            "GBP": "GBP",
            "British Pound": "GBP",
            "CAD": "CAD",
            "Canadian Dollar": "CAD",
            "AUD": "AUD",
            "Australian Dollar": "AUD",
            "JPY": "JPY",
            "Japanese Yen": "JPY",
            "CHF": "CHF",
            "Swiss Franc": "CHF",
            "CNY": "CNY",
            "Chinese Yuan": "CNY",
            "INR": "INR",
            "Indian Rupee": "INR"
        };

        // Get the ISO currency code, fallback to USD if not found
        const isoCurrency = currencyMap[currency] || "USD";

        try {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: isoCurrency
            }).format(amount);
        } catch (error) {
            console.warn(`Invalid currency code: ${isoCurrency}, falling back to USD`);
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD"
            }).format(amount);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10003] flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-xl w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-4"
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b p-4 sm:p-6 flex justify-between items-center">
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg flex-shrink-0">
                                    <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{t("paymentRequestModal.title")}</h2>
                                    <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">{t("paymentRequestModal.subtitle")}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-2"
                                disabled={isAccepting || isRejecting || overallLoading || loadingLogs}
                            >
                                <X className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                        </div>

                        {/* Full Screen Loading Overlay */}
                        {overallLoading && (
                            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10004] flex items-center justify-center p-4">
                                <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 flex flex-col items-center gap-4 max-w-sm sm:max-w-md mx-auto">
                                    <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600"></div>
                                    <div className="text-center">
                                        <p className="text-base sm:text-lg font-semibold text-gray-900">{t("paymentRequestModal.processing.title")}</p>
                                        <p className="text-xs sm:text-sm text-gray-600 mt-2 px-4">
                                            {isAccepting && t("paymentRequestModal.processing.updatingPayment")}
                                            {isRejecting && t("paymentRequestModal.processing.recordingRejection")}
                                            {isUpdatingEscrow && t("paymentRequestModal.processing.updatingEscrow")}
                                            {isCreatingChat && t("paymentRequestModal.processing.sendingNotification")}
                                        </p>
                                        <div className="mt-4 flex items-center justify-center gap-2">
                                            <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                                            <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Content */}
                        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">

                            {/* Loading State */}
                            {loadingLogs && (
                                <div className="flex flex-col sm:flex-row items-center justify-center py-4 gap-2 sm:gap-0">
                                    <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-purple-600"></div>
                                    <span className="text-sm sm:text-base text-gray-600">{t("paymentRequestModal.loading")}</span>
                                </div>
                            )}
                            {/* Payment Details */}
                            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                                    <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                                    {t("paymentRequestModal.paymentDetails")}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div>
                                        <label className="text-xs sm:text-sm font-medium text-gray-500">{t("paymentRequestModal.amount")}</label>
                                        <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                                            {formatCurrency((Number(user.role.id) === RoleEnum.client ? paymentRequest.escrow.baseAmount : paymentRequest.escrow.amount || 0), paymentRequest.currency)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs sm:text-sm font-medium text-gray-500">{t("paymentRequestModal.status")}</label>
                                        <p
                                            className={`text-xs sm:text-sm font-medium ${getPaymentRequestStatusClasses(paymentRequest.status)}`}
                                        >
                                            {getPaymentRequestStatusLabel(paymentRequest.status)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs sm:text-sm font-medium text-gray-500">{t("paymentRequestModal.requestedBy")}</label>
                                        <p className="text-xs sm:text-sm text-gray-900 flex items-center gap-2">
                                            <User className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                            <span className="truncate">{paymentRequest.escrow?.professional?.firstName + " " + paymentRequest.escrow?.professional?.lastName}</span>
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs sm:text-sm font-medium text-gray-500">{t("paymentRequestModal.requestDate")}</label>
                                        <p className="text-xs sm:text-sm text-gray-900 flex items-center gap-2">
                                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                            <span className="break-words">{formatDate(paymentRequest.createdAt || paymentRequest.requestDate)}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            {paymentRequest.description && (
                                <div>
                                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">{t("paymentRequestModal.description")}</h3>
                                    <p className="text-sm sm:text-base text-gray-700 bg-gray-50 rounded-lg p-3 sm:p-4 break-words">
                                        {paymentRequest.description}
                                    </p>
                                </div>
                            )}

                            {/* Attachments */}
                            {paymentRequest.attachments && paymentRequest.attachments.length > 0 && (
                                <div>
                                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">{t("paymentRequestModal.attachments")}</h3>
                                    <div className="space-y-2">
                                        {paymentRequest.attachments.map((attachment: any, index: number) => (
                                            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                                <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                                                <span className="text-xs sm:text-sm text-gray-700 truncate">{attachment.name || `${t("paymentRequestModal.attachment")} ${index + 1}`}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Reject Form */}
                            {showRejectForm && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="border border-red-200 rounded-lg p-4 bg-red-50"
                                >
                                    <div className="flex items-start gap-2 mb-3">
                                        <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-xs sm:text-sm font-medium text-red-800">{t("paymentRequestModal.rejectForm.title")}</h4>
                                            <p className="text-xs text-red-600">{t("paymentRequestModal.rejectForm.subtitle")}</p>
                                        </div>
                                    </div>
                                    <textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder={t("paymentRequestModal.rejectForm.placeholder")}
                                        className="w-full p-2 sm:p-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                        rows={3}
                                        disabled={isRejecting || overallLoading}
                                    />

                                    {/* File Upload Section */}
                                    <div className="mt-4">
                                        <label className="block text-xs sm:text-sm font-medium text-red-800 mb-2">
                                            {t("paymentRequestModal.rejectForm.attachDocuments")}
                                        </label>
                                        <div className="space-y-3">
                                            {/* File Input */}
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="file"
                                                    id="reasonAttachments"
                                                    multiple
                                                    onChange={handleFileInputChange}
                                                    className="hidden"
                                                    disabled={isRejecting || uploadingFiles || overallLoading}
                                                />
                                                <label
                                                    htmlFor="reasonAttachments"
                                                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 border border-red-300 rounded-lg cursor-pointer hover:bg-red-50 transition-colors text-xs sm:text-sm ${isRejecting || uploadingFiles || overallLoading ? "opacity-50 cursor-not-allowed" : ""
                                                        }`}
                                                >
                                                    {uploadingFiles ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-red-600"></div>
                                                            <span className="hidden sm:inline">{t("paymentRequestModal.rejectForm.uploading")}</span>
                                                            <span className="sm:hidden">{t("paymentRequestModal.rejectForm.uploadingShort")}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                                                            <span className="hidden sm:inline">{t("paymentRequestModal.rejectForm.uploadFiles")}</span>
                                                            <span className="sm:hidden">{t("paymentRequestModal.rejectForm.upload")}</span>
                                                        </>
                                                    )}
                                                </label>
                                            </div>

                                            {/* Uploaded Files List */}
                                            {reasonAttachments.length > 0 && (
                                                <div className="space-y-2">
                                                    <p className="text-xs sm:text-sm font-medium text-red-800">{t("paymentRequestModal.rejectForm.uploadedFiles")}</p>
                                                    {reasonAttachments.map((attachment, index) => (
                                                        <div key={index} className="flex items-center justify-between p-2 bg-red-100 rounded-lg">
                                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 flex-shrink-0" />
                                                                <span className="text-xs sm:text-sm text-red-800 truncate">{attachment.name}</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeAttachment(index)}
                                                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-2"
                                                                disabled={isRejecting || overallLoading}
                                                            >
                                                                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                        <Button
                                            onClick={handleReject}
                                            disabled={!rejectReason.trim() || isRejecting || overallLoading}
                                            className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm px-4 py-2 order-2 sm:order-1"
                                        >
                                            {isRejecting ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2"></div>
                                                    <span className="hidden sm:inline">{t("paymentRequestModal.rejectForm.rejecting")}</span>
                                                    <span className="sm:hidden">{t("paymentRequestModal.rejectForm.rejectingShort")}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="hidden sm:inline">{t("paymentRequestModal.rejectForm.confirmRejection")}</span>
                                                    <span className="sm:hidden">{t("paymentRequestModal.rejectForm.confirm")}</span>
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            onClick={handleCancelReject}
                                            variant="outline"
                                            disabled={isRejecting || overallLoading}
                                            className="text-xs sm:text-sm px-4 py-2 order-1 sm:order-2"
                                        >
                                            {t("paymentRequestModal.rejectForm.cancel")}
                                        </Button>
                                    </div>
                                </motion.div>
                            )}

                            {/* Current Payment Status */}
                            {/* {latestPaymentLog && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h3 className="text-lg font-medium text-blue-900 mb-2">Current Payment Status</h3>
                                    <div className="flex items-center gap-2">
                                        <div className={`w-3 h-3 rounded-full ${latestPaymentLog.paymentStatus?.id === MilestonePaymentStatusEnum.Released ? "bg-green-500" :
                                                latestPaymentLog.paymentStatus?.id === MilestonePaymentStatusEnum.Rejected ? "bg-red-500" :
                                                    latestPaymentLog.paymentStatus?.id === MilestonePaymentStatusEnum.Requested ? "bg-yellow-500" :
                                                        "bg-gray-500"
                                            }`}></div>
                                        <span className="text-sm font-medium text-blue-800">
                                            {latestPaymentLog.paymentStatus?.name || "Unknown Status"}
                                        </span>
                                    </div>
                                    {latestPaymentLog.reason && (
                                        <p className="text-sm text-blue-700 mt-2">
                                            <strong>Reason:</strong> {latestPaymentLog.reason}
                                        </p>
                                    )}
                                    {latestPaymentLog.reasonAttachments && latestPaymentLog.reasonAttachments.length > 0 && (
                                        <div className="mt-2">
                                            <p className="text-sm font-medium text-blue-800">Attachments:</p>
                                            <div className="space-y-1">
                                                {latestPaymentLog.reasonAttachments.map((attachment, index) => (
                                                    <div key={index} className="flex items-center gap-2 text-sm text-blue-700">
                                                        <FileText className="h-4 w-4" />
                                                        <span>{attachment.name || `Attachment ${index + 1}`}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )} */}

                            {/* Action Buttons */}
                            {!showRejectForm && Number(paymentRequest.escrow?.status?.id) === EscrowStatus.FUNDED && Number(user?.role?.id) === RoleEnum.client && (
                                <div className="pt-4 border-t">
                                    {(() => {
                                        console.log("PaymentRequestModal - latestPaymentLog:", latestPaymentLog);
                                        console.log("PaymentRequestModal - paymentStatus?.id:", latestPaymentLog?.paymentStatus?.id);
                                        console.log("PaymentRequestModal - MilestonePaymentStatusEnum.Requested:", MilestonePaymentStatusEnum.Requested);
                                        console.log("PaymentRequestModal - MilestonePaymentStatusEnum.Released:", MilestonePaymentStatusEnum.Released);

                                        if (Number(latestPaymentLog?.paymentStatus?.id) === MilestonePaymentStatusEnum.Released) {
                                            return (
                                                <div className="flex items-center justify-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                                                    <Check className="h-5 w-5 text-green-600" />
                                                    <span className="text-green-800 font-medium">{t("paymentRequestModal.statusMessages.alreadyReleased")}</span>
                                                </div>
                                            );
                                        } else if (Number(latestPaymentLog?.paymentStatus?.id) === MilestonePaymentStatusEnum.Rejected) {
                                            return (
                                                <div className="space-y-4">
                                                    {/* Rejection Details */}
                                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                                        <div className="flex items-start gap-2 mb-3">
                                                            <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                                                            <div>
                                                                <h4 className="text-sm font-medium text-red-800">{t("paymentRequestModal.statusMessages.previouslyRejected")}</h4>
                                                                <p className="text-xs text-red-600">{t("paymentRequestModal.statusMessages.changeDecision")}</p>
                                                            </div>
                                                        </div>

                                                        {/* Rejection Reason */}
                                                        {latestPaymentLog.reason && (
                                                            <div className="mb-3">
                                                                <p className="text-sm font-medium text-red-800 mb-1">{t("paymentRequestModal.statusMessages.previousReason")}</p>
                                                                <p className="text-sm text-red-700 bg-red-100 p-2 rounded">
                                                                    {latestPaymentLog.reason}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Rejection Attachments */}
                                                        {latestPaymentLog.reasonAttachments && latestPaymentLog.reasonAttachments.length > 0 && (
                                                            <div>
                                                                <p className="text-sm font-medium text-red-800 mb-2">{t("paymentRequestModal.statusMessages.previousDocuments")}</p>
                                                                <div className="space-y-2">
                                                                    {latestPaymentLog.reasonAttachments.map((attachment, index) => (
                                                                        <div key={index} className="flex items-center gap-2 p-2 bg-red-100 rounded-lg">
                                                                            <FileText className="h-4 w-4 text-red-600" />
                                                                            <span className="text-sm text-red-800">{attachment.name || `${t("paymentRequestModal.attachment")} ${index + 1}`}</span>
                                                                            {attachment.path && (
                                                                                <button
                                                                                    onClick={() => window.open(attachment.path, "_blank")}
                                                                                    className="ml-auto text-xs text-red-600 hover:text-red-800 underline"
                                                                                >
                                                                                    {t("paymentRequestModal.statusMessages.view")}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Accept and Reject Buttons */}
                                                    <div className="flex flex-col sm:flex-row gap-3">
                                                        <Button
                                                            onClick={handleAccept}
                                                            disabled={isAccepting || loading || loadingLogs || overallLoading}
                                                            className="flex-1 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 text-xs sm:text-sm px-4 py-3"
                                                        >
                                                            {isAccepting ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                                                                    <span className="hidden sm:inline">{t("paymentRequestModal.buttons.processing")}</span>
                                                                    <span className="sm:hidden">{t("paymentRequestModal.buttons.processingShort")}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                                                                    <span className="hidden sm:inline">{t("paymentRequestModal.buttons.acceptRelease")}</span>
                                                                    <span className="sm:hidden">{t("paymentRequestModal.buttons.acceptPayment")}</span>
                                                                </>
                                                            )}
                                                        </Button>

                                                        <Button
                                                            onClick={() => setShowRejectForm(true)}
                                                            disabled={isAccepting || loading || loadingLogs || overallLoading}
                                                            variant="outline"
                                                            className="flex-1 border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center gap-2 text-xs sm:text-sm px-4 py-3"
                                                        >
                                                            <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                                                            <span className="hidden sm:inline">{t("paymentRequestModal.buttons.rejectAgain")}</span>
                                                            <span className="sm:hidden">{t("paymentRequestModal.buttons.reject")}</span>
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        } else if (Number(latestPaymentLog?.paymentStatus?.id) === MilestonePaymentStatusEnum.Requested || Number(latestPaymentLog?.paymentStatus?.id) === MilestonePaymentStatusEnum.Funded) {
                                            return (
                                                <div className="flex flex-col sm:flex-row gap-3">
                                                    <Button
                                                        onClick={handleAccept}
                                                        disabled={isAccepting || loading || loadingLogs || overallLoading}
                                                        className="flex-1 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 text-xs sm:text-sm px-4 py-3"
                                                    >
                                                        {isAccepting ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                                                                <span className="hidden sm:inline">{t("paymentRequestModal.buttons.processing")}</span>
                                                                <span className="sm:hidden">{t("paymentRequestModal.buttons.processingShort")}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                                                                <span className="hidden sm:inline">{t("paymentRequestModal.buttons.acceptRelease")}</span>
                                                                <span className="sm:hidden">{t("paymentRequestModal.buttons.acceptPayment")}</span>
                                                            </>
                                                        )}
                                                    </Button>

                                                    <Button
                                                        onClick={() => setShowRejectForm(true)}
                                                        disabled={isAccepting || loading || loadingLogs || overallLoading}
                                                        variant="outline"
                                                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center gap-2 text-xs sm:text-sm px-4 py-3"
                                                    >
                                                        <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                                                        {t("paymentRequestModal.buttons.reject")}
                                                    </Button>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div className="text-center p-4 text-gray-500">
                                                    <span>{t("paymentRequestModal.statusMessages.noPaymentAvailable")}</span>
                                                    <div className="text-xs mt-1 text-gray-400">
                                                        {t("paymentRequestModal.statusMessages.statusLabel")}{" "}
                                                        {latestPaymentLog?.paymentStatus?.id
                                                            ? getMilestoneStatusLabel(latestPaymentLog.paymentStatus?.id)
                                                            : t("paymentRequestModal.statusMessages.noStatus")}{" "}
                                                        | {t("paymentRequestModal.statusMessages.expectedLabel")}{" "}
                                                        {getMilestoneStatusLabel(MilestonePaymentStatusEnum.Requested)}
                                                    </div>
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            )}

                            {/* Status Message */}
                            {paymentRequest.status !== "pending" && (
                                <div className={`p-3 sm:p-4 rounded-lg ${paymentRequest.status === "approved" ? "bg-green-50 border border-green-200" :
                                    paymentRequest.status === "rejected" ? "bg-red-50 border border-red-200" :
                                        "bg-gray-50 border border-gray-200"
                                    }`}>
                                    <div className="flex items-center gap-2">
                                        {paymentRequest.status === "approved" ? (
                                            <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                                        ) : paymentRequest.status === "rejected" ? (
                                            <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 flex-shrink-0" />
                                        ) : (
                                            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 flex-shrink-0" />
                                        )}
                                        <p className={`text-xs sm:text-sm font-medium ${paymentRequest.status === "approved" ? "text-green-800" :
                                            paymentRequest.status === "rejected" ? "text-red-800" :
                                                "text-gray-800"
                                            }`}>
                                            {t("paymentRequestModal.statusMessages.requestHasBeen", {
                                                status: getPaymentRequestStatusLabel(paymentRequest.status),
                                            })}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PaymentRequestModal;
